import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export function NumberedPager({
  page,
  pageSize,
  total,
  onPage,
}: {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
}) {
  const { t } = useTranslation('common');
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > pages) onPage(pages);
  }, [page, pages, onPage]);
  const numbers = [...new Set([1, page - 1, page, page + 1, pages])]
    .filter((n) => n >= 1 && n <= pages)
    .sort((a, b) => a - b);
  return (
    <nav aria-label={t('list.pager.label')} className="flex flex-wrap items-center gap-2">
      <span>{t('list.pager.total', { count: total })}</span>
      <Button variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {t('list.pager.previous')}
      </Button>
      {numbers.map((n, i) => (
        <span key={n}>
          {i > 0 && n - numbers[i - 1]! > 1 ? <span aria-hidden="true">… </span> : null}
          <Button
            variant={n === page ? 'default' : 'outline'}
            aria-current={n === page ? 'page' : undefined}
            onClick={() => onPage(n)}
          >
            {n}
          </Button>
        </span>
      ))}
      <Button variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        {t('list.pager.next')}
      </Button>
    </nav>
  );
}
